import React, {
  useState,
  useRef,
  useEffect,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Link,
  Box,
  Drawer,
  Collapse,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useMeta } from '../contexts/MetaContext';
import Menu from './Menu';
import { useNavigate } from 'react-router-dom';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const TopBar = ({ storeName, toggleTheme, mode, searchTerm, setSearchTerm }) => {
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const theme = useTheme();
  const searchInputRef = useRef(null);
  const { metaData } = useMeta();
  
  // Debounce the search term to avoid performance issues
  const debouncedSearchTerm = useDebounce(localSearchTerm, 200);
  
  // Update the parent's searchTerm only when debounced value changes
  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm, setSearchTerm]);

  const toggleSearch = () => {
    if (!localSearchTerm) {
      setIsSearchOpen(!isSearchOpen);
    } else if (!isSearchOpen) {
      setIsSearchOpen(true);
    }
  };

  // Add this useEffect hook to focus on the search input when it's opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  // Update local state immediately for responsive UI
  const handleSearchChange = (event) => {
    setLocalSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    setSearchTerm('');
    if (isSearchOpen) {
      setIsSearchOpen(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    navigate(`/search?q=${encodeURIComponent(debouncedSearchTerm)}`);
    setIsSearchOpen(false);
  };


  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          bgcolor: theme.palette.mode === 'light' ? 'white' : 'background.default',
          color: theme.palette.mode === 'light' ? 'text.primary' : 'text.primary',
          borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleMenu}
            >
              <MenuIcon />
            </IconButton>
            <IconButton color="inherit" onClick={toggleSearch} aria-label="search">
              <SearchIcon />
            </IconButton>
          </Box>
          <Typography variant="h6" component="div" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            <Link component={RouterLink} to="/" color="inherit" underline="none">
              {metaData.storeName}
            </Link>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" aria-label="shopping cart">
              <ShoppingCartIcon />
            </IconButton>
            <IconButton onClick={toggleTheme} color="inherit">
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Collapse in={isSearchOpen || !!localSearchTerm}>
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ p: 2, bgcolor: 'background.paper' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search products..."
            value={localSearchTerm}
            onChange={handleSearchChange}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: localSearchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Collapse>
      <Drawer 
        anchor="left" 
        open={isMenuOpen} 
        onClose={toggleMenu}
        PaperProps={{
          sx: {
            p: 1,
            width: 320,
            bgcolor: theme.palette.mode === 'light' ? 'background.paper' : 'background.default',
            color: theme.palette.text.primary,
          }
        }}
      >
        <Menu onMenuClose={handleMenuClose} />
      </Drawer>
    </Box>
  );
};

export default TopBar;